<template>
  <!-- send video link -->
  <div
    v-if="!isLoading && !hasCaption"
    class="relative white p-4 flex flex-col justify-center h-auto items-start w-full lg:w-[50%] xl:w-[40%] shadow-2xl rounded-xl py-4"
  >
    <!-- <p class="text-[#061B44] font-semibold text-xl pt-2">
      {{ $t("YouTube_Transcription_Made_Easy") }}
    </p>

    <p class="text-slate-500 font-medium text-sm pt-1">
      {{ $t("Enter_a_YouTube_video_link_an") }}
    </p> -->
    <!-- <p class="text-slate-900 font-semibold text-sm pt-4 pb-1">
      {{ $t("youtube-video-link") }}
    </p> -->
    <input
      type="text"
      v-model="userInput"
      @input="fetchVideoLanguage"
      @keydown.ctrl.x.exact="handleCtrlX"
      @keydown.enter.exact="transcriptVideo"
      placeholder="https://www.youtube.com/watch?v="
      :class="{ 'pl-4': locale != 'ar', 'pr-4': locale === 'ar' }"
      class="text-sm md:text-base text-field w-full mt-4 lg:mt-1 pb-4 h-10 text-start pl-4 pt-4 lg:pt-3 outline-none font-medium bg-white border border-slate-700 rounded-lg px-2"
    />
    <p class="font-normal text-xs text-center md:text-start md:text-sm text-slate-600 pt-2 mx-auto">
      {{ $t("supports-all-public-youtube-v") }} |
      {{ $t("max-duration-8-hours") }}
    </p>
    <!-- <p class="text-slate-900 font-semibold text-sm pb-1">
      {{ $t("source-language") }}
    </p> -->
    <button
      @click="toggleOriginalLanguageList"
      :disabled="isFetchingLanguage"
      :class="{
        'space-x-reverse': locale === 'ar',
        'opacity-50 cursor-not-allowed': isFetchingLanguage,
      }"
      class="w-full flex justify-center items-center space-x-4 bg-white h-10 p-3 rounded-lg border border-slate-900 mt-3 mb-1"
    >
      <p class="text-slate-900 font-medium text-xs md:text-base lg:text-lg">
        {{ $t("source-language") }}
      </p>
      <template v-if="!isFetchingLanguage">
        <span
          class="inline-flex items-center gap-1.5"
          :class="{ 'flex-row-reverse': locale === 'ar' }"
        >
          <i
            class="pi pi-globe text-slate-700 opacity-80 flex-shrink-0 text-sm md:text-base"
            aria-hidden="true"
          ></i>
          <span class="text-slate-900 font-medium text-xs md:text-base lg:text-lg">
            {{ removeParentheses(originalLanguage.langName) }}
          </span>
        </span>
      </template>
      <template v-else>
        <img
          class="w-[16px] md:w-[20px] animate-spin"
          src="../../../assets/icons/loading-loader.svg"
          alt="Loading"
        />
        <p class="text-slate-900 font-medium text-xs md:text-base lg:text-lg">
          {{ $t("detecting-language") }}
        </p>
      </template>
      <img
        v-if="!isFetchingLanguage"
        src="../../../assets/images/audio/02.svg"
        alt=""
        class="w-[2%] text-slate-900 fill-slate-900"
      />
    </button>
    <div
      v-if="
        authorLangSelected &&
        originalLanguage.langCode !== authorLanguage.langCode
      "
      class="flex flex-row space-x-2 p-2 md:p-4 border-[1px] border-orange-300 bg-orange-50 rounded-md mt-3 text-xs md:text-sm items-center justify-center text-orange-600 w-full"
    >
      <i class="pi pi-info-circle"></i>
      <p>
        {{ $t("the-selected-language-differs-from-the") }}
        ( {{ authorLanguage.langName }} ).
        {{ $t("transcription-quality-may-vary") }}
      </p>
    </div>
    <!-- One-time order: show notice; backend processes via PayPal webhook -->
    <div v-if="oneTimeOrderId" class="w-full max-w-md mx-auto flex flex-col items-center py-3 px-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 mt-3 mb-1">
      <template v-if="oneTimeIsYoutube">
        <p class="text-sm text-slate-600 mb-2">{{ $t("One-time YouTube payment success message") }}</p>
        <router-link
          v-if="oneTimeOrderId"
          to="/orders"
          class="text-sm font-medium text-subDarkBlue hover:underline"
        >
          {{ $t("View order status") }}
        </router-link>
      </template>
      <template v-else>
        <p v-if="!oneTimePaymentConfirmed" class="text-sm text-slate-600 mb-2 flex items-center gap-2">
          <span class="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          {{ $t("Confirming your payment...") }}
        </p>
        <p v-else class="text-sm text-slate-600 mb-2">{{ $t("Payment successful. Submit the video below to start processing.") }}</p>
      </template>
    </div>
    <button
      v-else-if="
        isValidYouTubeUrl(userInput) &&
        userInput.trim() &&
        !profileStore.hasActivePaidSubscription
      "
      type="button"
      @click="showOneTimePayPanel = true"
      class="w-full max-w-md mx-auto flex justify-center py-2.5 px-4 rounded-lg border-2 border-subDarkBlue text-subDarkBlue font-medium text-sm mt-3 mb-1 hover:bg-subWhite/50 transition-colors"
    >
      {{ $t("Pay once for this video") }}
    </button>
    <template v-if="oneTimeOrderId && oneTimeIsYoutube">
      <router-link
        to="/orders"
        class="button px-20 py-1 mt-4 bg-subDark text-white text-sm md:text-base lg:text-lg font-bold w-full rounded-lg h-10 flex items-center justify-center"
      >
        {{ $t("View order status") }}
      </router-link>
    </template>
    <button
      v-else
      @click="transcriptVideo"
      :disabled="isFetchingLanguage || (!!oneTimeOrderId && (!oneTimePaymentConfirmed || oneTimeConfirmSubmitting))"
      :class="{
        'opacity-50 cursor-not-allowed': isFetchingLanguage || (!!oneTimeOrderId && (!oneTimePaymentConfirmed || oneTimeConfirmSubmitting)),
      }"
      class="button px-20 py-1 mt-4 bg-subDark text-white text-sm md:text-base lg:text-lg font-bold w-full rounded-lg h-10"
    >
      {{ oneTimeOrderId && oneTimeConfirmSubmitting ? $t("Redirecting...") : oneTimeOrderId ? $t("Start processing") : $t("Transcript_Video") }}
    </button>
    <!-- translation language gridview -->
    <div v-if="isTargetListOpen" class="overlay z-50" @click="closeList">
      <div
        class="overlay-content flex flex-col items-center min-w-[80%] h-[70%]"
        @click.stop
      >
        <div
          :class="{ 'space-x-reverse': locale === 'ar' }"
          class="flex flex-row space-x-4 w-full items-center justify-center h-10 my-4"
        >
          <input
            v-model="searchQuery"
            @input="filteredLanguages"
            type="text"
            :placeholder="$t('search-for-a-language')"
            class="pl-4 bg-white border border-slate-900 rounded-md text-slate-900 text-base font-semibold w-[40%] h-10"
          />
          <button
            @click="closeList"
            class="bg-white border border-slate-900 rounded-md text-slate-900 text-lg font-bold py-1 px-8 h-10"
          >
            {{ $t("close") }}
          </button>
        </div>
        <ul
          class="py-2 h-75vh grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 overflow-y-auto"
        >
          <div
            v-for="lang in myLanguagesList"
            :key="lang.langName"
            class="flex flex-row space-x-2"
          >
            <a
              @click="selectTargetLanguage(lang)"
              class="flex items-center gap-2 px-4 py-2 text-gray-800 font-semibold cursor-pointer"
            >
              <i
                class="pi pi-globe text-gray-500 flex-shrink-0"
                style="font-size: 0.875rem"
                aria-hidden="true"
              ></i>
              {{ lang.langName }}
            </a>
          </div>
        </ul>
      </div>
    </div>
    <!-- original language gridview -->
    <div v-if="isOpen" class="overlay z-50" @click="closeList">
      <div
        class="overlay-content flex flex-col items-center min-w-[80%] h-[70%]"
        @click.stop
      >
        <div
          :class="{ 'space-x-reverse': locale === 'ar' }"
          class="flex flex-row space-x-4 w-full items-center justify-center h-10 my-4"
        >
          <input
            v-model="searchQuery"
            @input="filteredLanguages"
            type="text"
            :placeholder="$t('search-for-a-language')"
            class="pl-1 md:pl-4 bg-white border border-slate-900 rounded-md text-slate-900 text-xs md:text-base font-semibold w-[60%] md:w-[40%] h-10"
          />
          <button
            @click="closeList"
            class="bg-white border border-slate-900 rounded-md text-slate-900 text-sm md:text-lg font-bold py-1 px-8 h-10"
          >
            {{ $t("close") }}
          </button>
        </div>
        <ul
          class="py-1 h-75vh grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-4 gap-2 md:gap-4 overflow-y-auto"
        >
          <li
            v-for="lang in myLanguagesList.filter(
              (lang) => !unsupportedCloudStt.includes(lang.langCode)
            )"
            :key="lang.langName"
            class="flex flex-row space-x-2"
          >
            <a
              @click="selectLanguage(lang)"
              class="flex items-center gap-2 px-4 py-2 text-gray-800 font-semibold cursor-pointer text-sm md:text-base"
            >
              <i
                class="pi pi-globe text-gray-500 flex-shrink-0"
                style="font-size: 0.875rem"
                aria-hidden="true"
              ></i>
              {{ lang.langName }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <!-- premium service Alert -->
  <!-- <p
    v-if="premiumService && !isLoading && !computePricing && balanceNotEnough"
    class="text-xl pt-12 pb-4 text-center lg:text-center text-[#0077FF] font-semibold w-[90%] lg:w-[70%]"
  >
    {{ $t("oops-this-youtube-video-doesnt-have", [audioTranscriptCost]) }}
  </p> -->
  <!-- <p
    v-if="premiumService && !isLoading && computePricing"
    class="text-base pl-4 text-red-600 font-medium"
  >
    {{ $t("calculating-text-translation-cost-please-wait") }}
  </p> -->
  <!-- <div
    v-if="premiumService && balanceNotEnough && !computePricing"
    class="text-base pl-4 text-red-600 font-medium"
  >
    <p class="text-base pl-4 text-red-600 font-medium">
      {{ $t("your-balance-is-insufficient-please-recharge-to-continue") }}
    </p>
    <button @click="navToPayment" class="text-lg font-bold text-blue-600">
      {{ $t("clicking-here") }}
    </button>
  </div> -->
  <button
    v-if="premiumService && !isLoading"
    @click="() => transcriptPremiumVideo()"
    class="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full"
  >
    {{ $t("transcript-now") }}
  </button>
  <!-- processing status -->
  <div
    v-if="isLoading && !notReadyYet"
    class="relative white p-4 flex flex-col items-center justify-center h-auto w-[90%] lg:w-[50%] xl:w-[40%] shadow-2xl rounded-xl py-10 space-y-2"
  >
    <img
      class="w-12 h-12 lg:w-20 lg:h-20 animate-spin fill-subDark"
      src="../../../assets/icons/loading-loader.svg"
      alt="Loading icon"
    />
    <p class="text-[#061B44] font-semibold text-lg md:text-xl">
      {{ $t("retrieving-captions") }}
    </p>
    <!-- <p class="text-slate-500 font-medium text-sm">
      {{ $t("this-may-take-a-minute-hang-tight") }}
    </p> -->
  </div>
  <div
    class="rounded-2xl shadow-2xl p-6 w-[90%] lg:w-[65%] min-h-96 flex flex-col justify-center items-center space-y-2 h-auto"
    v-if="isLoading && notReadyYet && !hasCaption"
  >
    <img
      class="w-32 h-32 lg:w-40 lg:h-40 fill-[#061B44]"
      src="../../../assets/images/youtube/01.png"
      alt="Loading icon"
    />
    <p
      class="text-base pt-10 lg:text-xl text-[#061B44] text-center font-medium"
    >
      {{ $t("Your_video_was_sent_to_our_service_You_will_be") }}
    </p>
    <p class="text-base lg:text-xl text-[#061B44] text-center font-medium">
      {{
        $t(
          "thank-you-for-using-our-service-feel-free-to-continue-exploring-our-services"
        )
      }}
      <router-link to="/orders" class="font-semibold text-[#6193F6]">{{
        $t("Orderspage")
      }}</router-link>
    </p>
    <p class="text-base lg:text-xl text-[#061B44] text-center font-medium">
      {{ $t("if-youd-like-to-return-to-the-home-page-simply-follow") }}
      <button @click="goBackToMainPage" class="font-semibold text-[#6193F6]">
        {{ $t("the-link-below") }}
      </button>
    </p>
  </div>
  <!-- One-time pay panel (YouTube) — hidden for active subscribers (same as audio/video) -->
  <div
    v-if="showOneTimePayPanel && !profileStore.hasActivePaidSubscription"
    class="overlay bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4"
    @click.self="showOneTimePayPanel = false"
  >
    <div class="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <OneTimeAudioPay
        source-type="youtube"
        :youtube-link="userInput"
        :source-lang="originalLanguage?.langCode ?? ''"
        @cancel="showOneTimePayPanel = false"
      />
    </div>
  </div>
  <!-- user consume solde -->
  <div
    v-if="showPaymentModal"
    class="overlay bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
      <div class="flex flex-row text-xl items-center mb-4 space-x-1">
        <i class="pi pi-credit-card text-blue-500 text-center"></i>
        <h3 class="font-semibold">{{ $t("complete-your-translation") }}</h3>
      </div>
      <p class="text-gray-700 mb-6">{{ modalMessage }}</p>
      <div class="flex justify-end space-x-4">
        <button
          @click="handleModalResponse(false)"
          class="mt-4 bg-white text-slate-900 border-slate-400 border-[1px] h-9 py-1 px-10 rounded-md font-normal text-base"
        >
          {{ t("cancel") }}
        </button>
        <button
          @click="handleModalResponse(true)"
          class="mt-4 text-white bg-slate-900 h-9 py-1 px-10 rounded-md font-normal text-base flex items-center space-x-2"
        >
          <p>{{ $t("continue") }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useYoutubeStore } from "../../../store/youtubeStore";
import { useUserActivities } from "../../../store/userActivities";
import { useAuthStore } from "../../../store/auth";
import { useUserProfile } from "../../../store/userProfile";
import { useCommunicationStore } from "../../../store/connectionStore";
import {
  Language,
  languages,
  unsupportedCloudStt,
} from "../../../utils/languageList";
import { CaptionModel } from "../../../utils/captionModel";
import videojs from "video.js";
import "videojs-youtube";
import "video.js/dist/video-js.css";
import "videojs-youtube/dist/Youtube.min.js";
import { useRouter, useRoute } from "vue-router";
import {
  API_BASE_URL,
  GET_CAPTIONS,
  GETPRICES,
  TRANSCRIPT_YOUTUBE,
  TRANSLATE_TEXT,
  YTVIDEOLANGUAGE,
} from "../../../utils/appConst";
import OneTimeAudioPay from "../audio/one_time_audio_pay.vue";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";
import servicesPricing from "../../../utils/servicesPricing";

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const ys = useYoutubeStore();
const authStore = useAuthStore();
const profileStore = useUserProfile();
const activitesStore = useUserActivities();
const communicationStore = useCommunicationStore();
const isLoading = computed(() => ys.isLoading);
const hasCaption = computed(() => ys.hasCaption);
const userInput = computed({
  get: () => ys.userInput,
  set: (value) => (ys.userInput = value),
});
const textTranslationPrice = computed(() => ys.textTranslationPrice);
const notReadyYet = computed(() => ys.notReadyYet);
const premiumService = computed(() => ys.premiumService);
const isFromNotification = computed({
  get: () => ys.isFromNotification,
  set: (value) => (ys.isFromNotification = value),
});
const isTargetListOpen = computed({
  get: () => ys.isTargetListOpen,
  set: (value) => (ys.isTargetListOpen = value),
});
const balanceNotEnough = computed({
  get: () => ys.balanceNotEnough,
  set: (value) => (ys.balanceNotEnough = value),
});
const computePricing = computed(() => ys.computePricing);
const translationLanguage = computed({
  get: () => ys.translationLanguage,
  set: (value) => (ys.translationLanguage = value),
});
const authorLangSelected = computed({
  get: () => ys.authorLangSelected,
  set: (value) => (ys.authorLangSelected = value),
});
const isTargetLanguageSelected = computed(() => ys.isTargetLanguageSelected);
const myLanguagesList = computed(() => ys.myLanguagesList);
const originalLanguage = computed(() => ys.originalLanguage);
const authorLanguage = computed(() => ys.authorLanguage);
const isOpen = computed({
  get: () => ys.isOpen,
  set: (value) => (ys.isOpen = value),
});
const searchQuery = computed({
  get: () => ys.searchQuery,
  set: (value) => (ys.searchQuery = value),
});
const showPaymentModal = ref(false);
const modalMessage = ref("");
const isFetchingLanguage = ref(false);
const showOneTimePayPanel = ref(false);
watch(
  () => profileStore.hasActivePaidSubscription,
  (active) => {
    if (active) showOneTimePayPanel.value = false;
  }
);
const oneTimeOrderId = ref<string | null>(null);
const oneTimePaymentConfirmed = ref(false);
const oneTimeConfirmSubmitting = ref(false);
const oneTimeAutoStarted = ref(false);
/** True when this one-time order is YouTube and backend auto-starts processing (no Submit). */
const oneTimeIsYoutube = ref(false);
const transcriptYoutubeUrl = `${API_BASE_URL}${TRANSCRIPT_YOUTUBE}`;
const fetchYoutubeLanguageUrl = `${API_BASE_URL}${YTVIDEOLANGUAGE}`;
const getCpationUrl = `${API_BASE_URL}${GET_CAPTIONS}`;
const translateTextUrl = `${API_BASE_URL}${TRANSLATE_TEXT}`;
const getPrices = `${API_BASE_URL}${GETPRICES}`;

/** Matches getCaptions NO_YOUTUBE_CAPTIONS and legacy error strings (same idea as free_youtube_component). */
function isGetCaptionsNoCaptionsError(result: {
  code?: string;
  error?: unknown;
}): boolean {
  if (result?.code === "NO_YOUTUBE_CAPTIONS") return true;
  const err = result?.error;
  if (typeof err !== "string") return false;
  return (
    err === "No captions found for the specified video" ||
    err === "No captions found" ||
    err.includes("No captions found")
  );
}

const closeList = () => {
  isTargetListOpen.value = false;
  ys.searchQuery = "";
  ys.myLanguagesList = languages;
  isOpen.value = false;
};

const handleModalResponse = async (confirmed: boolean) => {
  if (confirmed) {
    await transcriptPremiumVideo(true);
  }
  showPaymentModal.value = false;
};

var videoJsOptions = {
  loadingSpinner: false,
  errorDisplay: true,
  preload: "auto",
  playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3],
  controls: true,
  textTrackSettings: true,
  textTrackDisplay: true,
  controlBar: {
    pictureInPictureToggle: true,
    remainingTimeDisplay: false,
    fullscreenToggle: true,
    volumePanel: {
      inline: true,
    },
  },
  html5: {
    nativeTextTracks: true,
    preloadTextTracks: false,
  },
  techOrder: ["html5", "youtube"],
};

function tryAutoStartYoutubeOneTime() {
  if (oneTimeIsYoutube.value) return;
  if (
    oneTimeOrderId.value &&
    userInput.value?.trim() &&
    originalLanguage.value &&
    !oneTimeConfirmSubmitting.value &&
    !oneTimeAutoStarted.value
  ) {
    oneTimeAutoStarted.value = true;
    toast(t("Your order is being processed. You'll be notified when ready."), {
      autoClose: 3000,
      type: "success",
      theme: "light",
    });
    router.push("/orders");
  }
}

function onOneTimePaymentSucceeded(e: CustomEvent<{ orderId: string }>) {
  if (e.detail?.orderId && e.detail.orderId === oneTimeOrderId.value) oneTimePaymentConfirmed.value = true;
}

function applyOneTimeReturnFromPayPal() {
  const q = route.query;
  if (q.onetime === "success" && q.orderId && q.type === "youtube") {
    const orderId = String(q.orderId);
    oneTimeOrderId.value = orderId;
    oneTimeIsYoutube.value = true;
    oneTimePaymentConfirmed.value = true;
    oneTimeAutoStarted.value = false;
    try {
      const savedLink = sessionStorage.getItem("onetime_youtube_link");
      const savedLang = sessionStorage.getItem("onetime_youtube_sourceLang");
      if (savedLink) ys.userInput = savedLink;
      if (savedLang) {
        const found = languages.find((l) => l.langCode === savedLang);
        if (found) {
          ys.originalLanguage = { ...found };
          ys.authorLanguage = { ...found };
          ys.authorLangSelected = true;
        }
      }
    } catch (_) {}
  } else {
    oneTimeOrderId.value = null;
    oneTimeIsYoutube.value = false;
    oneTimePaymentConfirmed.value = false;
    oneTimeAutoStarted.value = false;
  }
  if (q.onetime === "cancelled" && q.type === "youtube") {
    toast(t("Payment was cancelled."), { autoClose: 2500, type: "info", theme: "light" });
  }
}

watch(
  () => [route.query.onetime, route.query.orderId, route.query.type],
  () => applyOneTimeReturnFromPayPal(),
  { immediate: true }
);

watch(oneTimePaymentConfirmed, (v) => {
  if (v) tryAutoStartYoutubeOneTime();
});

onMounted(async () => {
  applyOneTimeReturnFromPayPal();
  window.addEventListener("oneTimePaymentSucceeded", onOneTimePaymentSucceeded as EventListener);
  ys.videoPlayer = videojs(
    "videoPlayer",
    videoJsOptions,
    function onPlayerReady() {
      videojs.log("Your player is ready!");
    }
  );
});

onUnmounted(() => {
  window.removeEventListener("oneTimePaymentSucceeded", onOneTimePaymentSucceeded as EventListener);
  try {
    ys.videoPlayer?.dispose();
    ys.videoPlayer = null;
  } catch (error) {
    console.log(`onUnmounted: ${error}`);
  }
});

const goBackToMainPage = () => {
  if (ys.notReadyYet && ys.isLoading) {
    ys.notReadyYet = false;
    ys.isLoading = false;
  }
};

const filteredLanguages = () => {
  if (searchQuery.value.length == 0) {
    ys.myLanguagesList = languages;
  } else {
    const lowerCaseQuery = searchQuery.value.toLowerCase();
    ys.myLanguagesList = languages.filter((langs) =>
      langs.langName.toLowerCase().includes(lowerCaseQuery)
    );
  }
};
const selectTargetLanguage = async (lang: Language) => {
  if (lang.langCode == ys.originalLanguage.langCode) {
    toast(t("original-and-translation-languages-must-be-different"), {
      autoClose: 1500,
      type: "error",
      theme: "light",
    });
    return;
  }
  translationLanguage.value = { ...lang };
  ys.isTranslationListOpened = false;
  ys.isTargetLanguageSelected = true;
  closeList();
};

const selectLanguage = (lang: Language) => {
  ys.originalLanguage = { ...lang };
  isOpen.value = false;
};

const navToPayment = () => {
  try {
    activitesStore.setComingFrom("youtubetranscription");
    router.push("/pricing");
  } catch (error) {
    console.log("navToPayment", error);
  }
};

function sttCostFromDeposit(minutes: number) {
  return minutes * servicesPricing.speechToText;
}

const transcriptPremiumVideo = async (extendPayment?: boolean) => {
  try {
    const userSubscription = profileStore.subscriptionType;
    if (userSubscription === "FREE") {
      toast(t("subscription-required-please-upgrade-your-plan"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (userSubscription != "CONSUMEASYOUPAY") {
      const currentTimestamp = new Date(new Date().toISOString());
      const subscriptionEndDate = new Date(profileStore.subscriptionEndDate);
      if (currentTimestamp > subscriptionEndDate) {
        toast(
          t("your-subscription-has-expired-please-renew-your-subscription"),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
        return;
      }
    }
    ys.hasCaption = false;
    ys.isLoading = true;
    const videoId = extractVideoId(ys.userInput);
    const getData = await transcriptYoutubeVideo(extendPayment);
    console.log("new request ---------------->", getData);

    if (getData.result && getData.result.captions) {
      ys.captions = getData.result.captions;
      ys.activeCaptionLang = originalLanguage.value.langName;
      const newCaptions: any = JSON.parse(JSON.stringify(ys.captions));
      const index = ys.availableCaptions.findIndex(
        (caption) => caption.language === originalLanguage.value.langName
      );
      if (index !== -1) {
        ys.availableCaptions[index].captions = newCaptions;
      } else {
        ys.availableCaptions.push({
          language: originalLanguage.value.langName,
          captions: newCaptions,
        });
      }
      ys.originalJsonContent = JSON.stringify(getData.result.captions);
      ys.originalText = extractText(getData.result.captions);
      ys.defaultOriginalText = extractText(getData.result.captions);
      ys.originalVideoSubtitle = subtitleGenerationFromCaption(
        getData.result.captions
      );
      ys.prebuildVideoSubtitle = subtitleGenerationFromCaption(
        getData.result.captions
      );
      ys.defaultOriginalVideoSubtitle = subtitleGenerationFromCaption(
        getData.result.captions
      );
      ys.currentRequestId = getData.request;
      /* if (isTargetLanguageSelected.value) {
        await translateText();
      } */
      ys.hasCaption = true;
    } else if (getData.result && isSonioxFormat(getData.result)) {
      // Soniox/Assembly format: { id, text, tokens } - caption exists, style soniox/assembly
      const resultData = getData.result;
      ys.originalText = extractTextFromSoniox(resultData);
      ys.defaultOriginalText = extractTextFromSoniox(resultData);
      const sonioxSubtitle = subtitleGenerationSoniox(resultData);
      ys.originalVideoSubtitle = sonioxSubtitle;
      ys.prebuildVideoSubtitle = sonioxSubtitle;
      ys.defaultOriginalVideoSubtitle = sonioxSubtitle;
      ys.originalJsonContent = JSON.stringify(resultData);
      ys.currentRequestId = getData.request || "";
      const parsedCaptions = parseWebVTTToCaption(sonioxSubtitle);
      ys.captions = parsedCaptions;
      const newCaptions = JSON.parse(JSON.stringify(parsedCaptions));
      const index = ys.availableCaptions.findIndex(
        (caption) => caption.language === originalLanguage.value.langName
      );
      if (index !== -1) {
        ys.availableCaptions[index].captions = newCaptions;
      } else {
        ys.availableCaptions.push({
          language: originalLanguage.value.langName,
          captions: newCaptions,
        });
      }
      ys.activeCaptionLang = originalLanguage.value.langName;
      ys.parsedCaptionsWithSpeakers = parseSonioxTranscription(resultData);
      ys.hasCaption = true;
    } else if (getData.result) {
      console.log("here ------------------>");
      ys.hasCaption = false;
      if (Array.isArray(getData.result) && getData.result.length === 0) {
        toast(t("this-video-doesnt-h"), {
          autoClose: 2500,
          type: "warning",
          theme: "light",
        });
        ys.isLoading = false;
      } else {
        ys.notReadyYet = true;
      }
      return;
    } else if (getData.error) {
      const transcriptErr =
        typeof getData.error === "string" ? getData.error : "";
      switch (getData.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        case "Subscription required. Please upgrade your plan.":
          toast(t("subscription-required-please-upgrade-your-plan"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          ys.isLoading = false;
          break;
        case "Your subscription has expired. Please renew your subscription.":
          toast(
            t("your-subscription-has-expired-please-renew-your-subscription"),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
          ys.isLoading = false;
          break;
        case "This is not a valid YouTube link.":
          ys.isNotValidLink = true;
          toast(
            t(
              "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
            ),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
          ys.isLoading = false;
          break;
        case "Both the video Link and the original language are required in the request body.":
          toast(t("no-languages-selected"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          ys.isLoading = false;
          break;
        case "The balance is insufficient":
          const videoUnits = getData.videoUnits || 0;
          if (userSubscription === "CONSUMEASYOUPAY") {
            const cost = sttCostFromDeposit(videoUnits / 2);
            const currentTimestamp = new Date(new Date().toISOString());
            const subscriptionEndDate = new Date(
              profileStore.subscriptionEndDate
            );
            if (currentTimestamp > subscriptionEndDate) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              ys.isLoading = false;
              break;
            }

            const shortfallCost = cost - profileStore.userDeposit;
            const shortfallChars = Math.ceil(
              shortfallCost / servicesPricing.speechToText
            );
            if (shortfallChars > profileStore.speechToTextVolume) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              ys.isLoading = false;
              break;
            }

            modalMessage.value = t("your-deposit-balance-is-not-enough");
            showPaymentModal.value = true;
            ys.isLoading = false;
            break;
          } else {
            const shortfallCost =
              videoUnits / 2 - profileStore.speechToTextVolume;
            const shortfallChars = sttCostFromDeposit(shortfallCost);
            if (shortfallChars > profileStore.userDeposit) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              ys.isLoading = false;
              break;
            }

            modalMessage.value = t("your-subscription-balance-is-not");
            showPaymentModal.value = true;
            ys.isLoading = false;
            break;
          }
        case "Audio duration exceeds maximum":
          toast(t("this-clip-exceeds-the-8-h"), {
            autoClose: 3500,
            type: "warning",
            theme: "light",
          });
          ys.isLoading = false;
          break;
        default:
          if (
            transcriptErr.includes("Video duration exceeds maximum") ||
            transcriptErr.includes("maximum allowed length")
          ) {
            toast(t("this-clip-exceeds-the-8-h"), {
              autoClose: 3500,
              type: "warning",
              theme: "light",
            });
          } else {
            toast(t("were-fixing-a-technical-issue-so-please-try"), {
              autoClose: 3000,
              type: "error",
              theme: "light",
            });
          }
          ys.isLoading = false;
          break;
      }
      return;
    }
    ys.isLoading = false;
    ys.mountPlayer = true;
    communicationStore.sendValue(videoId || "");
    try {
      if (ys.player != null) {
        ys.player.loadVideoById(videoId);
      } else {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        //initYouTubePlayer(videoId || "");
        communicationStore.sendValue(videoId || "");
      }
    } catch (error) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
      console.log("error when init------------", error);
      //initYouTubePlayer(videoId || "");
      communicationStore.sendValue(videoId || "");
    }
  } catch (error) {
    console.log(error);
  }
};
const extractVideoId = (url: string): string | null => {
  const videoIdMatch = url.match(
    /(?:youtube\.com\/(?:shorts\/|live\/|watch\?v=|embed\/|v\/|e\/|.*[?&]v=)|youtu\.be\/|youtube-nocookie\.com\/embed\/|m\.youtube\.com\/(?:watch\?v=|shorts\/|live\/))([a-zA-Z0-9_-]{11})/
  );
  return videoIdMatch ? videoIdMatch[1] : null;
};

const transcriptYoutubeVideo = async (extendPayment?: boolean) => {
  try {
    const response = await fetch(transcriptYoutubeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        videoLink: ys.userInput,
        languageCode: originalLanguage.value.langCode,
        alternativeLanguageCodes: [],
        extendPayment: extendPayment || false,
      }),
    });
    const videoCaptions = await response.json();
    if (!response.ok) {
      switch (videoCaptions.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          break;
      }
      return videoCaptions;
    }
    return videoCaptions;
  } catch (error) {
    console.error("Error making request to the server:", error);
  }
};

const extractText = (data: any) => {
  try {
    let concatenatedTranscript = "";
    data.forEach((caption: any) => {
      let text = caption["text"]!.trim();
      concatenatedTranscript += text + " ";
    });
    return concatenatedTranscript.trim();
  } catch (error) {
    console.log(error);

    return "";
  }
};

function subtitleGenerationFromCaption(captionsResponse: any): string {
  try {
    const captions: CaptionModel[] = [];
    captionsResponse.forEach((result: any) => {
      if (!result.text) {
        return;
      }
      const text = result.text.trim();
      if (text.length === 0) {
        return;
      }
      const newCaption: CaptionModel = {
        start: formatTime(parseFloat(result.start)),
        dur: formatTime(parseFloat(result.start) + parseFloat(result.dur)),
        text: text,
      };
      captions.push(newCaption);
    });

    // Format captions into WebVTT
    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, index) => {
      webvttContent += `${index + 1}\n${caption.start} --> ${caption.dur}\n${
        caption.text
      }\n\n`;
    });
    return webvttContent;
  } catch (error) {
    console.error("Error processing captions:", error);
    return "";
  }
}

const initYouTubePlayer = (videoId: string) => {
  ys.player = new window.YT.Player("ytPlayer", {
    width: "100%",
    height: "100%",
    videoId: videoId,
    playerVars: {
      //controls: 0,
    },
    events: {
      onReady: () => {
        ys.hasVideo = true;
        ys.isLoading = false;
      },
      onStateChange: (event: any) => {
        if (event.data === 1) {
          if (ys.intervalId !== null) {
            clearInterval(ys.intervalId);
          }
          ys.intervalId = setInterval(() => {
            ys.currentTime = ys.player?.getCurrentTime() || 0;
            updateCurrentCaptions(ys.currentTime);
          }, 1000);
        } else if (event.data === 0 || event.data === 2) {
          if (ys.intervalId !== null) {
            clearInterval(ys.intervalId);
            ys.intervalId = null;
          }
        }
      },
    },
  });
};

const updateCurrentCaptions = (currentTime: number) => {
  if (!Array.isArray(ys.captions)) {
    return;
  }
  ys.currentCaptions = [];
  ys.captions.forEach((element) => {
    const start = parseFloat(element.start);
    const end = start + parseFloat(element.dur);
    if (currentTime >= start && currentTime <= end) {
      ys.currentCaptions.push(element);
    }
  });
};

const fetchVideoLanguage = async () => {
  try {
    if (isValidYouTubeUrl(userInput.value)) {
      const videoId = extractVideoId(userInput.value);
      if (videoId) {
        isFetchingLanguage.value = true;
        if (authorLangSelected.value) {
          authorLangSelected.value = false;
        }
        try {
          const getData = await fetch(fetchYoutubeLanguageUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "ngrok-skip-browser-warning": "69420",
            },
            body: JSON.stringify({
              videoId: videoId,
            }),
          });

          if (!getData.ok) {
            throw new Error(`HTTP error! Status: ${getData.status}`);
          }
          const data = await getData.json();
          if (data.language && data.language != "Not specified") {
            const lng = data.language.includes("-")
              ? data.language.split("-")[0]
              : data.language;
            // First try exact match
            let foundLanguage = languages.find((language) => language.langCode === lng);
            
            // If not found, try to match by first part (e.g., "zh" matches "zh-CN", "zh-TW")
            if (!foundLanguage) {
              foundLanguage = languages.find((language) => 
                language.langCode.startsWith(lng + "-") || language.langCode === lng
              );
            }
            
            if (foundLanguage && foundLanguage.langName) {
              originalLanguage.value.langName = foundLanguage.langName;
              originalLanguage.value.langCode = foundLanguage.langCode;
              originalLanguage.value.countryCode = foundLanguage.countryCode;
              authorLanguage.value.langName = foundLanguage.langName;
              authorLanguage.value.langCode = foundLanguage.langCode;
              authorLanguage.value.countryCode = foundLanguage.countryCode;
              authorLangSelected.value = true;
            } else {
              originalLanguage.value.langName = "English";
              originalLanguage.value.langCode = "en";
              originalLanguage.value.countryCode = "us";
            }
          } else {
            originalLanguage.value.langName = "English";
            originalLanguage.value.langCode = "en";
            originalLanguage.value.countryCode = "us";
          }
        } catch (error) {
          console.log(`fetchVideoLanguage: ${error}`);
          // Set default language on error
          originalLanguage.value.langName = "English";
          originalLanguage.value.langCode = "en";
          originalLanguage.value.countryCode = "us";
        } finally {
          isFetchingLanguage.value = false;
        }
      } else {
        isFetchingLanguage.value = false;
      }
    } else {
      isFetchingLanguage.value = false;
    }
  } catch (error) {
    console.log(`fetchVideoLanguage: ${error}`);
    isFetchingLanguage.value = false;
  }
};

const handleCtrlX = (event: any) => {
  if (event.key === "x" && event.ctrlKey) {
    event.preventDefault();
    const inputElement = event.target;
    inputElement.select();
    document.execCommand("cut");
  }
};

const transcriptVideo = async () => {
  try {
    if (!authStore.token || authStore.token.length == 0) {
      authStore.setAuthPop(true);
      return;
    }
    if (userInput.value.trim().length == 0) {
      return;
    }
    if (oneTimeOrderId.value) {
      toast(t("Your order is being processed. You'll be notified when ready."), {
        autoClose: 3000,
        type: "success",
        theme: "light",
      });
      router.push("/orders");
      return;
    }
    ys.notReadyYet = false;
    ys.isNotValidLink = false;
    ys.hasCaption = false;
    ys.premiumService = false;
    ys.currentRequestId = "";
    // check if valid youtube link
    if (isValidYouTubeUrl(userInput.value)) {
      ys.isLoading = true;
      const videoId = extractVideoId(userInput.value);
      if (!videoId) {
        ys.isLoading = false;
        ys.isNotValidLink = true;
        if (isValidYouTubePlaylistUrl(userInput.value)) {          
          toast(t("playlist-links-are-not-supported-please"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
        } else {
          toast(
            t(
              "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
            ),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
        }
        ys.userInput = "";
        return;
      }
      ys.isNotValidLink = false;
      const response = await fetch(getCpationUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          videoLink: ys.userInput,
          videoOriginLang: originalLanguage.value.langCode,
          // TODO add alternative list
        }),
      });

      const result = await response.json();
      const errMsg =
        typeof result.error === "string" ? result.error : "";

      if (isGetCaptionsNoCaptionsError(result)) {
        const userSubscription = profileStore.subscriptionType;
        if (userSubscription === "FREE") {
          ys.isLoading = false;
          toast(t("subscription-required-please-upgrade-your-plan"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          return;
        }
        if (userSubscription != "CONSUMEASYOUPAY") {
          const currentTimestamp = new Date(new Date().toISOString());
          const subscriptionEndDate = new Date(
            profileStore.subscriptionEndDate
          );
          if (currentTimestamp > subscriptionEndDate) {
            ys.isLoading = false;
            toast(
              t(
                "your-subscription-has-expired-please-renew-your-subscription"
              ),
              {
                autoClose: 2500,
                type: "error",
                theme: "light",
              }
            );
            return;
          }
        }
        await transcriptPremiumVideo();
      } else if (
        errMsg.includes("This is not") ||
        errMsg.includes("Video ID must")
      ) {
        ys.isNotValidLink = true;
        ys.userInput = "";
        ys.isLoading = false;
        toast(
          t(
            "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
          ),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
        return;
      } else if (errMsg.includes("Language must")) {
        ys.isLoading = false;
        toast(t("no-languages-selected"), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
        return;
      } else if (result.error) {
        ys.isLoading = false;
        toast(t("were-fixing-a-technical-issue-so-please-try"), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
        return;
      } else {
        console.log("result -----------------> 5");
        // the video has already native captions
        ys.captions = result.captions;
        ys.activeCaptionLang = originalLanguage.value.langName;
        const newCaptions = JSON.parse(JSON.stringify(ys.captions));
        const index = ys.availableCaptions.findIndex(
          (caption) => caption.language === originalLanguage.value.langName
        );
        if (index !== -1) {
          ys.availableCaptions[index].captions = newCaptions;
        } else {
          ys.availableCaptions.push({
            language: originalLanguage.value.langName,
            captions: newCaptions,
          });
        }
        ys.originalJsonContent = JSON.stringify(result.captions);
        ys.originalText = extractText(result.captions);
        ys.defaultOriginalText = extractText(result.captions);
        ys.originalVideoSubtitle = subtitleGenerationFromCaption(
          result.captions
        );
        ys.prebuildVideoSubtitle = subtitleGenerationFromCaption(
          result.captions
        );
        ys.defaultOriginalVideoSubtitle = subtitleGenerationFromCaption(
          result.captions
        );
        if (
          originalLanguage.value.langCode === translationLanguage.value.langCode
        ) {
          if (originalLanguage.value.langCode === "ar") {
            translationLanguage.value = {
              langName: "English",
              langCode: "en",
              countryCode: "us",
            };
          } else {
            translationLanguage.value = {
              langName: "Arabic",
              langCode: "ar",
              countryCode: "sa",
            };
          }
        }
        ys.hasCaption = true;
        ys.isLoading = false;
        //if (isTargetLanguageSelected.value) {
        //  await translateText();
        //}
        ys.mountPlayer = true;
        try {
          if (ys.player != null) {
            ys.player.loadVideoById(videoId);
          } else {
            //initYouTubePlayer(videoId || "");
            communicationStore.sendValue(videoId || "");
          }
        } catch (error) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const firstScriptTag = document.getElementsByTagName("script")[0];
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
          console.log("error when init------------", error);
          //initYouTubePlayer(videoId || "");
          communicationStore.sendValue(videoId || "");
        }
      }
    } else {
      ys.isNotValidLink = true;
      ys.userInput = "";
      toast(
        t(
          "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
        ),
        {
          autoClose: 2500,
          type: "error",
          theme: "light",
        }
      );
    }
    const checkboxes = document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox: any) => {
      if (checkbox.id === "none-checkbox") {
        checkbox.checked = true;
      }
    });
  } catch (error) {
    console.error("Error making request to the server:", error);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const toggleOriginalLanguageList = () => {
  /* if (isFromNotification.value) {
    return;
  } */
  isOpen.value = !isOpen.value;
};

const toggleTranslationLanguageList = () => {
  isTargetListOpen.value = !isTargetListOpen.value;
};

const translateText = async () => {
  try {
    const userSubscription = profileStore.subscriptionType;
    if (userSubscription === "FREE") {
      toast(t("subscription-required-please-upgrade-your-plan"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    const currentTimestamp = new Date(new Date().toISOString());
    const subscriptionEndDate = new Date(profileStore.subscriptionEndDate);
    if (currentTimestamp > subscriptionEndDate) {
      toast(t("your-subscription-has-expired-please-renew-your-subscription"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    await computeTranslationCost(true);
    if (balanceNotEnough.value) {
      return;
    }
    // translate text
    const response = await fetch(translateTextUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        originalText: ys.originalText,
        from: originalLanguage.value.langCode,
        to: translationLanguage.value.langCode,
        fromRequest: ys.currentRequestId != "" ? ys.currentRequestId : null,
      }),
    });

    const results = await response.json();
    if (!response.ok) {
      switch (results.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          break;
      }
      return;
    }
    if (results.translatedText) {
      ys.translatedText = results.translatedText;
      ys.defaultTranslatedText = results.translatedText;
    } else {
      ys.translatedText = t("translation-is-not-available");
    }
    await profileStore.getUserSubscription(authStore.token!);
    // todo update str player
  } catch (error) {
    console.error("Error making request to the server:", error);
  }
};

const isValidYouTubeUrl = (url: string): boolean => {
  const youtubeRegex =
    /^(https?:\/\/)?((www|m)\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)\/(watch\?v=|embed\/|v\/|shorts\/|live\/|playlist\?list=|[\w-]+\?.*)?([\w-]{11})(?:[&\?].*)?$/;
  return youtubeRegex.test(url);
};

const isValidYouTubePlaylistUrl = (url: string): boolean => {
  const youtubePlaylistRegex =
    /^(https?:\/\/)?((www\.)?youtube\.com)\/playlist\?list=([\w-]{18,})(?:[&\?].*)?$/;
  return youtubePlaylistRegex.test(url);
};

const computeTranscriptionCost = async () => {
  return new Promise<void>(async (resolve, reject) => {
    let newVideoPlayer: any;
    let dummyVideoElement: any;
    try {
      dummyVideoElement = document.createElement("video");
      dummyVideoElement.id = "dummy-video";
      dummyVideoElement.style.display = "none";
      document.body.appendChild(dummyVideoElement);
      newVideoPlayer = videojs(dummyVideoElement, {
        controls: true,
        autoplay: false,
        preload: "auto",
      });
      ys.videoPlayer?.off("loadedmetadata");
      ys.videoPlayer?.off("error");
      newVideoPlayer.on("loadedmetadata", async function () {
        try {
          const durationSeconds = newVideoPlayer.duration() ?? 0;
          let minutes = Math.floor(durationSeconds / 60);
          const remainingSeconds = durationSeconds % 60;
          if (remainingSeconds >= 30) {
            minutes++;
          }
          if (profileStore.speechToTextVolume < minutes) {
            ys.balanceNotEnough = true;
            toast(t("your-balance-of-stt"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
          } else {
            ys.balanceNotEnough = false;
          }
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          if (newVideoPlayer) {
            newVideoPlayer.dispose();
          }
          if (dummyVideoElement && document.body.contains(dummyVideoElement)) {
            document.body.removeChild(dummyVideoElement);
          }
        }
      });
      const uniqueSrc = `${userInput.value}?t=${Date.now()}`;
      newVideoPlayer.src({ type: "video/youtube", src: uniqueSrc });
      newVideoPlayer.on("error", function () {
        console.error("Video.js player error:", newVideoPlayer.error());
        reject(new Error("Video.js player error"));
      });
    } catch (error) {
      console.error("Error in computeTranscriptionCost:", error);
      if (newVideoPlayer) {
        newVideoPlayer.dispose();
      }
      if (dummyVideoElement && document.body.contains(dummyVideoElement)) {
        document.body.removeChild(dummyVideoElement);
      }
      reject(error);
    }
  });
};

const computeTranslationCost = async (translateText: boolean) => {
  try {
    ys.computePricing = true;
    ys.balanceNotEnough = false;
    let totalLength = 0;
    if (translateText) {
      totalLength = ys.originalText.length;
    } else {
      // captions case
      ys.captions.map((caption) => {
        totalLength += caption.text.length;
      });
    }
    if (profileStore.textTranslationVolume < totalLength) {
      ys.balanceNotEnough = true;
      toast(t("your-balance-is-insufficient-please-recharge-to-continue"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
    } else {
      ys.balanceNotEnough = false;
    }
    ys.computePricing = false;
  } catch (error) {
    console.log(error);
    ys.computePricing = false;
    ys.balanceNotEnough = false;
  }
};

const fetchTranslationPrice = async () => {
  try {
    const response = await fetch(getPrices, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    const result = await response.json();
    if (result.error) {
      ys.computePricing = false;
      return;
    }
    if (result.message == "Success" && result.Prices) {
      ys.textTranslationPrice = result.Prices.text_per_1000_char;
    }
  } catch (error) {
    console.log(error);
  }
};

const removeParentheses = (langName: string | undefined) => {
  if (!langName) {
    return "";
  }
  return langName.replace(/\s*\(.*?\)\s*/g, "").trim();
};

function formatTime(seconds: number): string {
  const date = new Date(seconds * 1000);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const secondsFormatted = date.getSeconds().toString().padStart(2, "0");
  const milliseconds = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);
  return `${hours}:${minutes}:${secondsFormatted}.${milliseconds}`;
}

// Soniox/Assembly format helpers (response with id, text, tokens)
function isSonioxFormat(data: any): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }
  if (
    data.id !== undefined &&
    data.text !== undefined &&
    Array.isArray(data.tokens)
  ) {
    return true;
  }
  return false;
}

const extractTextFromSoniox = (sonioxData: any): string => {
  try {
    if (sonioxData && typeof sonioxData === "object" && sonioxData.text) {
      return sonioxData.text || "";
    }
    if (sonioxData?.tokens && Array.isArray(sonioxData.tokens)) {
      let text = "";
      sonioxData.tokens.forEach((token: any) => {
        if (token.text) text += token.text;
      });
      return text.trim();
    }
    return "";
  } catch {
    return "";
  }
};

function parseSonioxTranscription(sonioxResponse: any): CaptionModel[] {
  try {
    const captions: CaptionModel[] = [];
    const minBinSize = 3.5;
    const maxBinSize = 4.5;
    if (!sonioxResponse.tokens || !Array.isArray(sonioxResponse.tokens)) {
      return captions;
    }
    let currentSpeaker: number | null = null;
    let currentStartTime = 0;
    let transcript = "";
    const speakerSegments: Array<{
      word: string;
      speakerTag: number | null;
      startTime: number;
      start: number;
      end: number;
    }> = [];
    const words: Array<{
      text: string;
      startMs: number;
      endMs: number;
      speaker: number | null;
    }> = [];
    let currentWord = "";
    let wordStartMs = 0;
    let wordEndMs = 0;
    let wordSpeaker: number | null = null;

    sonioxResponse.tokens.forEach((token: any) => {
      const tokenText = token.text || "";
      const tokenStartMs = token.start_ms || 0;
      const tokenEndMs = token.end_ms || 0;
      const tokenSpeaker =
        token.speaker != null
          ? typeof token.speaker === "string"
            ? parseInt(token.speaker)
            : token.speaker
          : null;
      const hasLeadingSpace = tokenText.trimStart() !== tokenText;
      const trimmedTokenText = tokenText.trimStart();
      if (hasLeadingSpace) {
        if (currentWord.trim()) {
          words.push({
            text: currentWord.trim(),
            startMs: wordStartMs,
            endMs: wordEndMs,
            speaker: wordSpeaker,
          });
        }
        currentWord = trimmedTokenText;
        wordStartMs = tokenStartMs;
        wordEndMs = tokenEndMs;
        wordSpeaker = tokenSpeaker;
      } else {
        if (!currentWord) {
          wordStartMs = tokenStartMs;
          wordSpeaker = tokenSpeaker;
        }
        currentWord += tokenText;
        wordEndMs = tokenEndMs;
        if (tokenSpeaker !== null) wordSpeaker = tokenSpeaker;
      }
    });
    if (currentWord.trim()) {
      words.push({
        text: currentWord.trim(),
        startMs: wordStartMs,
        endMs: wordEndMs,
        speaker: wordSpeaker,
      });
    }

    const getMostCommonSpeaker = (
      segments: Array<{ speakerTag: number | null }>
    ): number | null => {
      const speakerCounts: Record<number, number> = {};
      let maxCount = 0;
      let mostCommonSpeaker: number | null = null;
      segments.forEach((segment) => {
        if (segment.speakerTag != null) {
          speakerCounts[segment.speakerTag] =
            (speakerCounts[segment.speakerTag] || 0) + 1;
          if (speakerCounts[segment.speakerTag] > maxCount) {
            maxCount = speakerCounts[segment.speakerTag];
            mostCommonSpeaker = segment.speakerTag;
          }
        }
      });
      if (mostCommonSpeaker === null) {
        const firstSpeaker = segments.find((s) => s.speakerTag != null);
        if (firstSpeaker) mostCommonSpeaker = firstSpeaker.speakerTag;
      }
      return mostCommonSpeaker;
    };

    if (words.length === 0) return captions;

    words.forEach((word, i) => {
      const wordStartSeconds = word.startMs / 1000;
      const nextWord = words[i + 1];
      const nextWordStartSeconds = nextWord ? nextWord.startMs / 1000 : null;
      const wordText = word.text;
      const speakerTag = word.speaker;
      if (transcript.trim() === "") {
        currentStartTime = wordStartSeconds;
        currentSpeaker = speakerTag;
      }
      const speakerChanged =
        currentSpeaker !== null &&
        speakerTag !== null &&
        currentSpeaker !== speakerTag;
      if (
        speakerChanged &&
        transcript.trim() !== "" &&
        speakerSegments.length > 0
      ) {
        const duration = wordStartSeconds - currentStartTime;
        if (duration >= 0.3) {
          const captionSpeaker = getMostCommonSpeaker(speakerSegments);
          captions.push({
            start: formatTime(currentStartTime),
            dur: formatTime(wordStartSeconds),
            text: transcript.trim(),
            speakerTag: captionSpeaker,
            words: speakerSegments.map((s) => ({
              word: s.word,
              speakerTag: s.speakerTag,
              startTime: s.startTime,
            })),
          });
          currentStartTime = wordStartSeconds;
          transcript = "";
          speakerSegments.length = 0;
          currentSpeaker = speakerTag;
        }
      }
      transcript = transcript + " " + wordText;
      speakerSegments.push({
        word: wordText,
        speakerTag: speakerTag,
        startTime: wordStartSeconds,
        start: wordStartSeconds,
        end: word.endMs / 1000,
      });
      if (speakerTag !== null) currentSpeaker = speakerTag;
      if (nextWordStartSeconds !== null) {
        const duration = nextWordStartSeconds - currentStartTime;
        const ndnextWord = words[i + 2];
        const ndnextWordStartSeconds = ndnextWord
          ? ndnextWord.startMs / 1000
          : null;
        const ndDuration =
          ndnextWordStartSeconds != null
            ? ndnextWordStartSeconds - nextWordStartSeconds + duration
            : null;
        const shouldSplit =
          (minBinSize <= duration && duration <= maxBinSize) ||
          (ndDuration != null && ndDuration > maxBinSize);
        if (shouldSplit) {
          const captionSpeaker = getMostCommonSpeaker(speakerSegments);
          captions.push({
            start: formatTime(currentStartTime),
            dur: formatTime(nextWordStartSeconds),
            text: transcript.trim(),
            speakerTag: captionSpeaker,
            words: speakerSegments.map((s) => ({
              word: s.word,
              speakerTag: s.speakerTag,
              startTime: s.startTime,
            })),
          });
          currentStartTime = nextWordStartSeconds;
          transcript = "";
          speakerSegments.length = 0;
          currentSpeaker = speakerTag;
        }
      }
    });
    if (transcript.trim() !== "" && words.length > 0) {
      const lastWord = words[words.length - 1];
      const lastWordEndSeconds = lastWord.endMs / 1000;
      const duration = lastWordEndSeconds - currentStartTime;
      if (duration > 0) {
        const captionSpeaker = getMostCommonSpeaker(speakerSegments);
        captions.push({
          start: formatTime(currentStartTime),
          dur: formatTime(lastWordEndSeconds),
          text: transcript.trim(),
          speakerTag: captionSpeaker,
          words: speakerSegments.map((s) => ({
            word: s.word,
            speakerTag: s.speakerTag,
            startTime: s.startTime,
          })),
        });
      }
    }
    return captions;
  } catch {
    return [];
  }
}

function subtitleGenerationSoniox(sonioxResponse: any): string {
  try {
    const captions = parseSonioxTranscription(sonioxResponse);
    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, index) => {
      webvttContent += `${index + 1}\n${caption.start} --> ${caption.dur}\n${caption.text}\n\n`;
    });
    return webvttContent;
  } catch {
    return "";
  }
}

function convertTimeToSeconds(time: string): number {
  const parts = time.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseFloat(parts[2].replace(",", "."));
  return hours * 3600 + minutes * 60 + seconds;
}

function parseWebVTTToCaption(webvttContent: string): any[] {
  try {
    const captions: any[] = [];
    const webvttLines = webvttContent.split("\n").map((line) => line.trim());
    let index = 0;
    while (index < webvttLines.length) {
      const line = webvttLines[index];
      if (line === "WEBVTT" || line === "" || /^\d+$/.test(line)) {
        index++;
        continue;
      }
      const timeCode = webvttLines[index];
      const [start, dur] = timeCode.split(" --> ");
      const textLines: string[] = [];
      index++;
      while (index < webvttLines.length && webvttLines[index] !== "") {
        textLines.push(webvttLines[index]);
        index++;
      }
      captions.push({
        start: convertTimeToSeconds(start),
        dur: (
          convertTimeToSeconds(dur) - convertTimeToSeconds(start)
        ).toFixed(3),
        text: textLines.join(" "),
      });
      index++;
    }
    return captions;
  } catch {
    return [];
  }
}
</script>
